import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'react-redux';

import store from './redux/store';
import { getUser, refreshAccessToken } from './utils/helpers';
import Preloader from './components/Global/Preloader';
import { csrfService } from './services/csrfService';

import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import GitHubCallback from './components/Auth/GitHubCallback';
import OAuthCallback from './components/Auth/OAuthCallback';
import Workspaces from './components/Workspaces';
import UserProfile from './components/Profile/UserProfile';
import Workspace from './components/Profile/Workspace';
import Boards from './components/Profile/Boards';
import Cards from './components/Profile/CardTable';
import Collaborators from './components/Profile/Collaborators';
import Settings from './components/Profile/Settings';
import WhiteLabelSettings from './components/Profile/WhiteLabelSettings';
import Notifications from './components/Workspaces/Notifications';

import Modal from './components/Global/ModalManager';
import PopOver from './components/Global/PopOver';

import ExpandableColumn from './components/Global/ExpandableColumn';
import TimerManager from './components/TimerManager/TimerManager';
import TimerToggleButton from './components/TimerManager/TimerToggleButton';

import { BoardProvider } from './context/BoardContext';

import ExportImport from './components/Profile/ExportImport';
import BoardWrapper from './components/Workspaces/BoardWrapper';
import Activity from './components/Profile/Activity';

import './App.scss';
import './components/Global/enhanced-scrollbar.css';
import SnackBar from './components/Global/SnackBar';
import VerificationSuccess from './components/Auth/VerificationSuccess';
import VerificationError from './components/Auth/VerificationError';
import VerificationRequired from './components/Auth/VerificationRequired';
import { fetchUser } from './redux/Slices/thunks';
import Reset from './components/Auth/Reset';
import RecoveryMail from './components/Auth/RecoveryMail';
import Verify from './components/Auth/Verify';
import JoinBoard from './components/Members/JoinBoard';
import AcceptInvitationRequest from './components/Members/AcceptInvitationRequest';
import SetPassword from './components/Auth/SetPassword';
import SetPasswordBanner from './components/Auth/PasswordBanner';
import { fetchWorkspaces } from './redux/Slices/workspaceSlice';
import { fetchRunningTimers } from './redux/Slices/timerSlice';
import FeedbackButton from './components/Global/FeedbackButton';
import Home from './pages/Home';
import Tooltip from './components/Global/Tooltip';
import MetaTags from './components/Global/MetaTags';
import OnlineStatusProvider from './context/OnlineStatusContext';
import PushNotification from './components/Global/PushNotification';
import Slack from './components/Auth/Slack';
import FontProvider from './components/FontProvider';
import Policy from './pages/Policy';
import Terms from './pages/Terms';
import OnboardingFlow from './components/Import/OnboardingFlow';
import ImportProgress from './components/Import/ImportProgress';
import Pricing from './pages/Billing';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancel';
import Checkout from './pages/Checkout';
import NetworkStatus from './components/Global/NetworkStatus';
import AnalyticsPage from './components/Analytics/AnalyticsPage';
import { config } from './config';
import AIChatbot from './components/AI/AIChatbot';
import NotFound from './components/400';
import DomainDebug from './components/Global/DomainDebug';
import Sidebar from './components/Sidebar';
import GlobalNavbar from './components/Global/GlobalNavbar';
import FeaturesComparison from './pages/FeaturesComparison';
import About from './pages/About';
import ThemeDebugger from './components/Debug/ThemeDebugger';
import Contact from './pages/Contact';
import { useWhiteLabel } from './context/WhiteLabelContext';
import WorkspaceSettings from './components/Profile/WorkspaceSettings';

import { closeModal } from './redux/Slices/modalSlice';
import { togglePopover } from './redux/Slices/popoverSlice';

const ProtectedRoute = ({ element: Component, loggedIn, verified, redirectTo = "/login", ...rest }) => {
  if (!loggedIn) return <Navigate to={redirectTo} />;
  if (!verified) return <Navigate to="/verification-required" />;
  return <Component {...rest} />;
};

// Component to handle home page routing based on white label status
const HomeOrLogin = ({ loggedIn, verified, userProfileBoardsLink }) => {
  const { isWhiteLabel: isWL } = useWhiteLabel();

  // If logged in, redirect to user boards
  if (loggedIn) {
    return <ProtectedRoute element={() => <Navigate to={userProfileBoardsLink} />} loggedIn={loggedIn} verified={verified} />;
  }

  // If white label domain, redirect to login instead of showing home page
  if (isWL()) {
    return <Navigate to="/login" />;
  }

  // Default: show home page for non-white-label domains
  return <Home />;
};

const UserProfileRedirect = () => {
  const { userName } = useParams();
  return <Navigate to={`/u/${userName}/profile`} replace />;
};

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [verified, setVerified] = useState();
  const [loading, setLoading] = useState(true);
  const [userProfileBoardsLink, setUserProfileBoardsLink] = useState('');
  const isCheckingLoginStatus = useRef(false);
  const { user } = useSelector((state) => state.user);
  const { settings } = user?.user || {};


  const dispatch = useDispatch();

  // Initialize CSRF protection on app start
  useEffect(() => {
    const initializeSecurity = async () => {
      try {
        await csrfService.initialize();
        console.log('✅ CSRF protection initialized');
      } catch (error) {
        console.warn('⚠️ CSRF protection initialization failed:', error);
        // App continues to work, CSRF tokens will be fetched on-demand
      }
    };

    initializeSecurity();
  }, []);

  useEffect(() => {
    const checkLoginStatus = async () => {
      if (isCheckingLoginStatus.current) return;

      isCheckingLoginStatus.current = true;
      const data = await getUser();

      if (data?.user?._id) {
        setLoggedIn(true);
        setVerified(data.user.verified || false);
        setUserProfileBoardsLink(`/u/${data.user.username}/boards`);
      }

      setLoading(false);
    };

    checkLoginStatus();
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) {
      dispatch(fetchUser());
    }
  }, [dispatch]);

  const { workspaces } = useSelector(state => state.workspaces);

  useEffect(() => {
    if (loggedIn && workspaces.length === 0) {
      dispatch(fetchWorkspaces()).unwrap().then((fetchedWorkspaces) => {
        if (!fetchedWorkspaces || fetchedWorkspaces.length === 0) {
        }
      }).catch((error) => {
        console.error('Error fetching workspaces:', error);
      });
    }
  }, [dispatch, workspaces.length]);

  // Fetch running timers when user is logged in
  useEffect(() => {
    if (loggedIn && verified) {
      dispatch(fetchRunningTimers()).catch((error) => {
        console.error('Error fetching running timers:', error);
      });
    }
  }, [loggedIn, verified, dispatch]);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    root.setAttribute("data-theme-color", settings.themeColor);

  }, [user]);

  if (loading) {
    return <Preloader loading={loading} />; // You might want to display a loading spinner here
  }

  return (
    <Provider store={store}>
      <FontProvider>
        <Router>
          <OnlineStatusProvider>
            <AppContent loggedIn={loggedIn} verified={verified} userProfileBoardsLink={userProfileBoardsLink} user={user} />
          </OnlineStatusProvider>
        </Router>
      </FontProvider>
    </Provider>
  );
}

const AppContent = ({ loggedIn, verified, userProfileBoardsLink, user }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const modalIsOpen = useSelector((state) => state.modal.isOpen);
  const popoverState = useSelector((state) => state.popover);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (modalIsOpen) {
          dispatch(closeModal());
        }
        if (popoverState?.isVisible) {
          dispatch(togglePopover({
            contentId: popoverState.contentId,
            position: popoverState.position,
            targetId: popoverState.targetId,
            props: popoverState.props,
          }));
        }
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [dispatch, modalIsOpen, popoverState?.isVisible, popoverState?.contentId, popoverState?.targetId, popoverState?.position, popoverState?.props]);


  // Split the path and get the first non-empty segment
  const pathParts = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathParts[0];
  const lastPart = pathParts[pathParts.length - 1];

  // Define a mapping for segments to the desired pageId
  const pageIdMapping = {
    'c': 'card-page',
    'w': 'workspace-page',
    'u': 'user-page',
    'b': 'board-page',
    'boards': 'user-boards-page',
    'profile': 'user-page',
    'cards': 'card-page',
    'activity': 'activity-page',
    'settings': 'settings-page',
    'white-label': 'white-label-page',
    'members': 'members-page',
    'notifications': 'notifications-page',
    'billing': 'billing-page',
    'import': 'import-page',
    'progress': 'import-progress-page',
    'analytics': 'analytics-page',
  };

  // Prioritize lastPart first, then fallback to firstSegment
  const pageId = pageIdMapping[lastPart] || pageIdMapping[firstSegment] || 'home';

  return (
    <main id={pageId} className="app-content">
      {(loggedIn && pageId !== 'billing-page') && <Sidebar />}
      {(loggedIn && pageId !== 'billing-page') && <ExpandableColumn />}

      <div className="app-content-wrapper">
        {user && !user?.user?.isDefaultPasswordSet && !user?.user?.registeredWithGoogle && <SetPasswordBanner />}

        <GlobalNavbar />

        <MetaTags />

        <BoardProvider>
          <Routes>
            <Route path="/workspace" element={<ProtectedRoute element={Workspace} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/" element={<HomeOrLogin loggedIn={loggedIn} verified={verified} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/u/:userName" element={<UserProfileRedirect />} />
            <Route path="/u/:userName/profile" element={<ProtectedRoute element={UserProfile} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/boards" element={<ProtectedRoute element={Workspace} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/cards" element={<ProtectedRoute element={Cards} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/activity" element={<ProtectedRoute element={Activity} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/settings" element={<ProtectedRoute element={Settings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/white-label" element={<ProtectedRoute element={WhiteLabelSettings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:workspaceSlug" element={<ProtectedRoute element={Workspaces} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/:boardSlug" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/analytics" element={<ProtectedRoute element={AnalyticsPage} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/c/:cardId/:cardSlug" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/c/:cardId/" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/w/:shortId/boards" element={<ProtectedRoute element={Boards} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/members" element={<ProtectedRoute element={Collaborators} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/settings" element={<ProtectedRoute element={WorkspaceSettings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/notifications" element={<ProtectedRoute element={Notifications} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/login" element={!loggedIn ? <Login /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/signup" element={!loggedIn ? <Signup /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/auth/github/callback" element={<GitHubCallback />} />
            <Route path="/auth/oauth/callback" element={<OAuthCallback />} />
            <Route path="/verify" element={<Verify />} /> {/* Only one verify route */}
            <Route path="/recovery" element={!loggedIn ? <RecoveryMail /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/reset" element={!loggedIn ? <Reset /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/set-password" element={loggedIn ? <SetPassword /> : <Navigate to={'/login'} />} />

            <Route path="/slack/auth" element={loggedIn ? <Slack /> : <Navigate to={userProfileBoardsLink} />} />

            <Route path="/invite/b/:boardId" element={<AcceptInvitationRequest />} />
            <Route path="/invite/w/:shortId" element={<AcceptInvitationRequest />} />

            <Route path="/legal/privacy-policy" element={<Policy />} />
            <Route path="/legal/terms-of-service" element={<Terms />} />
            <Route path="/features-comparison" element={<FeaturesComparison />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/w/:shortId/billing" element={<ProtectedRoute element={Pricing} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/payment-success" element={<ProtectedRoute element={PaymentSuccess} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/payment-cancelled" element={<ProtectedRoute element={PaymentCancelled} loggedIn={loggedIn} verified={verified} />} />


            {
              !verified && (
                <>
                  <Route path="/verification-error" element={<VerificationError />} />
                  <Route path="/verification-required" element={<VerificationRequired />} />
                </>
              )
            }
            {
              verified && (
                <Route path="/verification-success" element={<VerificationSuccess />} />
              )
            }

            <Route path="/import" element={<ProtectedRoute element={OnboardingFlow} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/import/progress" element={<ProtectedRoute element={ImportProgress} loggedIn={loggedIn} verified={verified} />} />

            {/* Catch-all route for 404 pages */}
            {/* <Route path="*" element={<NotFound />} /> */}
          </Routes>

          <Modal />
          <SnackBar />
          <Tooltip />
          <PushNotification />
          <NetworkStatus />
          <TimerManager />
          <TimerToggleButton />
          <AIChatbot />
          <PopOver />

          {/* <DomainDebug /> */}
          {/* <ThemeDebugger /> */}
        </BoardProvider>
      </div>

    </main>
  );
};

export default App;